
import React from "react";
import { toast } from "sonner";
import FolderContent from "./FolderContent";
import FolderView from "@/components/Auth/Folders/FolderView/index";
import ProjectsSection from "./ProjectsSection";
import ProjectSection from "@/components/Auth/Folders/FolderView/components/ProjectSection";
import ProjectRootView from "@/components/Auth/Folders/ProjectRootView";
import ProjectOverviewPage from "./ProjectOverviewPage";

interface PageContentProps {
  isLoading: boolean;
  fetchError: string | null;
  projectId: string | null;
  selectedFolderId: string | null;
  activeTab: string;
  activeSubTab: string;
  user: any;
  isSharedFolder: boolean;
  setActiveSubTab: (tab: string) => void;
  handleSelectProject: (projectId: string) => Promise<string>;
  handleSelectSharedFolder: (folderId: string) => Promise<string>;
  handleCreateRootFolder: () => boolean;
  handleStartAssessment: (projectId: string, folderId: string) => void;
  handleBackToFolders: () => void;
  fetchProjectIdForSharedFolder: (folderId: string) => Promise<string | null>;
  handleNavigateToAssessment: (assessmentId: string, folderId: string) => void;
  handleCreateNewAssessment: (projectId: string, folderId: string) => void;
  refreshFolderTree?: () => void;
  onSelectFolder?: (folderId: string) => void;
  // Add new props for project overview
  projects?: any[];
  sharedProjects?: any[];
  onCreateProject?: (name: string) => Promise<void>;
  onDeleteProject?: (projectId: string) => Promise<boolean>;
}

const PageContent: React.FC<PageContentProps> = ({
  isLoading,
  projectId,
  selectedFolderId,
  activeTab,
  activeSubTab,
  user,
  isSharedFolder,
  setActiveSubTab,
  handleSelectProject,
  handleSelectSharedFolder,
  handleCreateRootFolder,
  handleStartAssessment,
  handleBackToFolders,
  fetchProjectIdForSharedFolder,
  handleNavigateToAssessment,
  handleCreateNewAssessment,
  refreshFolderTree,
  onSelectFolder,
  projects = [],
  sharedProjects = [],
  onCreateProject,
  onDeleteProject

}) => {
  // Handle starting a NEW risk assessment with proper validation
  const handleStartRiskAssessment = async (projectId: string, folderId?: string) => {
    if (!folderId) {
      toast.error("Kan inte starta risksammanställning", {
        description: "En mapp måste väljas först.",
        duration: 5000
      });
      return;
    }
    
    try {
      // For shared folders, resolve the projectId if needed
      let effectiveProjectId = projectId;
      
      if ((!effectiveProjectId || effectiveProjectId === '') && isSharedFolder) {
        console.log("Resolving project ID for shared folder:", folderId);
        effectiveProjectId = await fetchProjectIdForSharedFolder(folderId) || '';
        
        if (!effectiveProjectId) {
          toast.error("Kunde inte hitta projekt-ID för denna delade mapp");
          console.error("Failed to resolve project ID for shared folder:", folderId);
          return;
        }
        
        console.log("Resolved project ID for shared folder:", effectiveProjectId);
      }
      
      console.log("Starting NEW risk assessment with:", { 
        projectId: effectiveProjectId, 
        folderId, 
        isSharedFolder 
      });
      
      // Use the unified new assessment creation
      handleCreateNewAssessment(effectiveProjectId, folderId);
    } catch (error) {
      console.error("Error preparing to start assessment:", error);
      toast.error("Kunde inte förbereda risksammanställningen");
    }
  };

  // Handle project selection with proper async support
  const handleProjectSelectAsync = async (projectId: string): Promise<string> => {
    try {
      const result = await handleSelectProject(projectId);
      return result;
    } catch (error) {
      console.error("Error selecting project:", error);
      toast.error("Kunde inte välja projekt");
      return projectId;
    }
  };

  // Show folder view when a specific folder is selected
  if (selectedFolderId) {
    console.log("Rendering FolderView with:", {
      projectId: projectId || "",
      folderId: selectedFolderId,
      isSharedFolder
    });
    
    return (
      <div className="animate-fade-in">
        <FolderView
          projectId={projectId || ""} 
          folderId={selectedFolderId} 
          onNavigateToFolder={handleBackToFolders}
          onStartAssessment={handleStartAssessment}
          onNavigateToAssessment={(assessmentId, folderId) => {
            console.log("Navigating to EXISTING assessment:", { assessmentId, folderId });
            handleNavigateToAssessment(assessmentId, folderId);
          }}
          onCreateNewAssessment={(projectId, folderId) => {
            console.log("Creating NEW assessment from FolderView:", { projectId, folderId });
            handleCreateNewAssessment(projectId, folderId);
          }}
          fetchProjectIdForSharedFolder={fetchProjectIdForSharedFolder}
          isSharedFolder={isSharedFolder}
          onFolderUpdated={refreshFolderTree}
        />
      </div>
    );
  }

  // Show project root content when a project is selected but no specific folder
  if (projectId && !selectedFolderId) {
    console.log("Rendering ProjectRootView with projectId:", projectId);
    
    return (
      <div className="animate-fade-in">
        <ProjectRootView
          projectId={projectId}
          onSelectFolder={onSelectFolder || (() => {})}
          onStartAssessment={handleStartAssessment}
          onNavigateToAssessment={(assessmentId, folderId) => {
            console.log("Navigating to EXISTING assessment from project root:", { assessmentId, folderId });
            handleNavigateToAssessment(assessmentId, folderId);
          }}
          onCreateNewAssessment={(projectId, folderId) => {
            console.log("Creating NEW assessment from project root:", { projectId, folderId });
            handleCreateNewAssessment(projectId, folderId);
          }}
          refreshFolderTree={refreshFolderTree}
        />
      </div>
    );
  }

  // Show beautiful project overview when no project is selected
  return (
    <div className="animate-fade-in">
      <ProjectOverviewPage
        projects={projects}
        sharedProjects={sharedProjects}
        onSelectProject={async (projectId: string) => {
          await handleProjectSelectAsync(projectId);
        }}
        onCreateProject={onCreateProject}
        onDeleteProject={onDeleteProject}
        isLoading={isLoading}
      />
    </div>
  );
};

export default PageContent;
